import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Baby, User } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function GenderGuessGame({ eventId, isOwner }) {
  const [guesses, setGuesses] = useState([]);
  const [stats, setStats] = useState({ male: 0, female: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [guestName, setGuestName] = useState("");
  const [selectedGender, setSelectedGender] = useState(null);
  const [hasGuessed, setHasGuessed] = useState(false);

  useEffect(() => {
    fetchGuesses();
  }, [eventId]);

  const fetchGuesses = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/gender-guess`);
      setGuesses(response.data.guesses || []);
      setStats(response.data.stats || { male: 0, female: 0, total: 0 });
      
      // Check if user has already guessed
      const userGuess = response.data.guesses?.find(g => g.guest_name === guestName);
      if (userGuess) {
        setHasGuessed(true);
        setSelectedGender(userGuess.guess);
      }
    } catch (error) {
      console.error("Failed to fetch guesses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!guestName || !selectedGender) {
      toast.error("Bitte gib deinen Namen ein und wähle ein Geschlecht!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/gender-guess`,
        {
          guest_name: guestName,
          guess: selectedGender
        }
      );
      
      toast.success("Dein Tipp wurde gespeichert!");
      setHasGuessed(true);
      fetchGuesses();
    } catch (error) {
      console.error("Failed to submit guess:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  const malePercent = stats.total > 0 ? Math.round((stats.male / stats.total) * 100) : 50;
  const femalePercent = stats.total > 0 ? Math.round((stats.female / stats.total) * 100) : 50;

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Baby className="w-5 h-5 text-primary" />
          Geschlechter-Ratespiel
        </h3>
        <p className="text-sm text-muted-foreground">
          {isOwner 
            ? "Lass deine Gäste raten, ob es ein Junge oder ein Mädchen wird!"
            : "Rate mit: Wird es ein Junge oder ein Mädchen?"
          }
        </p>
      </div>

      {/* Stats */}
      {stats.total > 0 && (
        <div className="mb-6 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Gesamt: {stats.total} {stats.total === 1 ? 'Tipp' : 'Tipps'}</span>
          </div>
          
          {/* Progress Bars */}
          <div className="space-y-2">
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-blue-600 font-medium">👦 Junge</span>
                <span className="text-blue-600 font-bold">{malePercent}%</span>
              </div>
              <div className="h-3 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all duration-500"
                  style={{ width: `${malePercent}%` }}
                ></div>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-pink-600 font-medium">👧 Mädchen</span>
                <span className="text-pink-600 font-bold">{femalePercent}%</span>
              </div>
              <div className="h-3 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full bg-pink-500 transition-all duration-500"
                  style={{ width: `${femalePercent}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Guess Form (for visitors) */}
      {!isOwner && !hasGuessed && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="guest-name">Dein Name *</Label>
            <Input
              id="guest-name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="z.B. Max Mustermann"
              required
            />
          </div>

          <div>
            <Label className="mb-3 block">Dein Tipp *</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSelectedGender("male")}
                className={`p-6 rounded-xl border-2 transition-all ${
                  selectedGender === "male"
                    ? "border-blue-500 bg-blue-50"
                    : "border-border hover:border-blue-300"
                }`}
              >
                <div className="text-4xl mb-2">👦</div>
                <div className="font-medium">Junge</div>
              </button>
              
              <button
                type="button"
                onClick={() => setSelectedGender("female")}
                className={`p-6 rounded-xl border-2 transition-all ${
                  selectedGender === "female"
                    ? "border-pink-500 bg-pink-50"
                    : "border-border hover:border-pink-300"
                }`}
              >
                <div className="text-4xl mb-2">👧</div>
                <div className="font-medium">Mädchen</div>
              </button>
            </div>
          </div>

          <Button type="submit" className="w-full rounded-full">
            Tipp abgeben
          </Button>
        </form>
      )}

      {/* Already Guessed */}
      {!isOwner && hasGuessed && (
        <div className="p-4 rounded-lg bg-secondary/30 text-center">
          <p className="text-sm font-medium mb-1">Du hast bereits getippt!</p>
          <p className="text-xs text-muted-foreground">
            Dein Tipp: {selectedGender === "male" ? "👦 Junge" : "👧 Mädchen"}
          </p>
        </div>
      )}

      {/* Recent Guesses (for owner) */}
      {isOwner && guesses.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Letzte Tipps</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {guesses.slice(-10).reverse().map((guess, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{guess.guest_name}</span>
                </div>
                <span className="text-sm">
                  {guess.guess === "male" ? "👦 Junge" : "👧 Mädchen"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}